import { Vue, Component } from "vue-property-decorator";
import { isNavigationFailure, NavigationFailureType } from "vue-router";
import { Action, Mutation } from "vuex-class";
import { EVENT_BUS, ROUTER } from "../../common/constants";
import moduleConstant from "../../common/module-constant";
import { namespace } from "vuex-class";
import { storeModules } from "../../store";
import { ENQUIRY_CENTRE_ORIGIN } from "../../common/uri/property-uri.constants";
import EventBus from "../../utils/event-bus";
import { DrawerModel, IDrawerModel } from "../../models/common/drawer.model";
import { DRAWER_NAME } from "../../common/drawer-component.constants";
import { AgendaServices, PropertyServices, TaskServices } from "../../services";
import { ReportHelper } from "../../utils/report-helper";
import { resolveCreateTaskDrawer } from "./create-task-handoff";
import {
	ENQUIRY_EVENT,
	ENQUIRY_RESPONSE,
	ENQUIRY_TASK_CONTEXT_PREFIX,
	ENQUIRY_APPOINTMENT_CONTEXT_PREFIX,
	ENQUIRY_EDIT_APPOINTMENT_CONTEXT_PREFIX,
	ENQUIRY_KEEP_ALIVE_PROTOCOL
} from "./enquiry-events.constants";
import { getEnquiryCentreIframe } from "./frame";

const CommonMutation = namespace(storeModules.Common, Mutation);
const CommonAction = namespace(storeModules.Common, Action);

@Component({
	name: ROUTER.ENQUIRY_CENTRE,
	template: require("./template.html"),
	components: {
		'agenda-confirmation': () => import('../agenda/components/agenda-confirmation-component/view/web-app/AgendaConfirmationComponent.vue'),
		'agenda-add-comment': () => import('../agenda/components/agenda-add-comment-component/AgendaAddCommentComponent.vue'),
		'agenda-add-person': () => import('../agenda/components/agenda-add-person-component/AgendaAddPersonComponent.vue'),
		'email-out': () => import('../history/email-out/EmailOutComponent.vue')
	}
})
// The iframe itself lives in ./frame (mounted once in the app shell) so it
// survives tab switches; this route component renders the placeholder the frame
// overlays and hosts the drawers/dialogs the Enquiry app asks for.
export default class EnquiryCentre extends Vue {
	@CommonMutation setVisibleDrawerModel: (payload: IDrawerModel) => void;
	@CommonAction('setPrintTemplateModalVisible') setPrintTemplateModalVisible: (payload: { visible: boolean, props?: any }) => void;
	@CommonAction('setAgendaPrefetchProperty') storeSetAgendaPrefetchProperty: (payload: { Id: number, Info: object }) => Promise<any>;
	@CommonAction('setAgendaPrefetchPerson') storeSetAgendaPrefetchPerson: (payload: { Id: number, Info: object }) => Promise<any>;
	@CommonAction('setAgendaPrefetchTask') storeSetAgendaPrefetchTask: (payload: { Id: number, UserId: number }) => void;
	@CommonAction('resetAgendaPrefetchInfo') storeResetAgendaPrefetchInfo: (key?: string) => void;

	visibleConfirmAgenda = false;
	confirmAgendaId: string = null;
	visibleEmailOut = false;
	selectedEmailOut: { RootId: number } = null;
	visibleAddComment = false;
	addCommentEvent: any = null;
	visibleAddPerson = false;
	addPersonChild: any = null;
	// Enquiry email histories are person histories; the forward-mail payload and
	// history-detail drawer only understand Object/Person/Demand modules.
	emailOutModuleType = moduleConstant.Person;

	private taskService = new TaskServices();
	private propertyService = new PropertyServices();
	private agendaService = new AgendaServices();
	private pendingTaskContext: string = null;
	private pendingAppointmentContext: string = null;
	private pendingEditAppointmentContext: string = null;
	private pendingCommentContext: string = null;
	private pendingAddPersonContext: string = null;
	private isRedirectingToAgenda = false;
	private isComponentDestroyed = false;

	mounted() {
		window.addEventListener("message", this.handleMessage);

		EventBus.$on(EVENT_BUS.AGENDA.CREATE_NEW_TASK, this.onTaskCreatedFromEnquiry);
		EventBus.$on(EVENT_BUS.AGENDA.CREATE_NEW_APPOINTMENT, this.onAppointmentCreatedFromEnquiry);
	}

	// Acks carry `source` like every WebApp → Enquiry message of the keep-alive
	// contract, so the Enquiry app can filter on data.source uniformly.
	private postMessageToIframe(type: string) {
		const iframe = getEnquiryCentreIframe();
		if (iframe?.contentWindow) {
			iframe.contentWindow.postMessage({ source: ENQUIRY_KEEP_ALIVE_PROTOCOL.hostSource, type }, ENQUIRY_CENTRE_ORIGIN);
		}
	}

	private openTaskDrawer(props: object, title: string) {
		this.pendingTaskContext = `${ENQUIRY_TASK_CONTEXT_PREFIX}-${Date.now()}`;

		this.setVisibleDrawerModel(new DrawerModel({
			componentName: DRAWER_NAME.TASK_NEW_FORM,
			context: this.pendingTaskContext,
			props,
			title,
			showSaveBtn: true,
		}));
	}

	private async redirectToAgendaWithTask(taskId: number) {
		// Same rule as creating an appointment from the task list/detail: copy the
		// task's property + person into the agenda prefetch and carry the task and
		// its assignee, then open the calendar — the agenda switches to the
		// assignee's calendar and a slot pick loads the appointment from the task.

		// Ignore duplicate messages while a redirect is already in flight, so two overlapping
		// loads can't race and seed prefetch from the wrong task.
		if (this.isRedirectingToAgenda) return;
		this.isRedirectingToAgenda = true;

		try {
			const { userId, personId, personReference, propertyId, propertyReference } =
				await this.taskService.getAgendaPrefetchFields({ Id: taskId });

			// Clear any stale prefetch first so a prior property/person can't keep showing.
			this.storeResetAgendaPrefetchInfo();
			this.storeSetAgendaPrefetchTask({ Id: taskId, UserId: userId });
			// Seed sequentially: both actions write the shared agenda manager info, so running
			// them in parallel could clobber it non-deterministically.
			if (propertyId) {
				await this.prefetchTaskProperty({ Id: propertyId, Reference: propertyReference });
			}
			if (personId) {
				await this.storeSetAgendaPrefetchPerson({ Id: personId, Info: { Reference: personReference } });
			}
		} finally {
			this.isRedirectingToAgenda = false;
		}

		// The user may have navigated away while the task was loading; don't yank them back,
		// and clear the seeded prefetch so it can't leak into an unrelated later /agenda visit.
		if (this.isComponentDestroyed) {
			this.storeResetAgendaPrefetchInfo();
			return;
		}

		// If a drawer is open the global router guard prompts to close it before navigating,
		// so just push and let that standard confirmation flow handle it.
		// Declined by a guard (resolves with an aborted failure) or failed (rejects): roll
		// back the armed prefetch — a kept-alive agenda's watcher restores its calendar.
		// A duplicated or superseded push leaves the agenda showing, so keep it.
		const isDeclined = await this.$router.push({ name: ROUTER.AGENDA.AGENDA }).then(
			result => isNavigationFailure(result, NavigationFailureType.aborted),
			() => true
		);
		if (isDeclined) {
			this.storeResetAgendaPrefetchInfo();
		}
	}

	// Fetch the property detail so the prefetch panel can show visiting hours, like the
	// property detail flow. Falls back to the task's reference if the detail fails to load.
	private async prefetchTaskProperty(property: { Id: number, Reference?: string }) {
		let info: { Reference: string, VisitingHours?: string } = { Reference: property.Reference };
		try {
			const detail = await this.propertyService.getPropertyById(property.Id) as { Reference?: string, Visitinghours?: string };
			info = { Reference: detail.Reference || property.Reference, VisitingHours: detail.Visitinghours };
		} catch (error) {
			console.error('Failed to load property detail for prefetch, propertyId:', property.Id, error);
		}
		return this.storeSetAgendaPrefetchProperty({ Id: property.Id, Info: info });
	}

	private openPersonDetailDrawer(personId: number) {
		this.setVisibleDrawerModel(new DrawerModel({
			componentName: DRAWER_NAME.PERSON_DETAIL,
			props: { objectId: personId },
			title: 'PERSON',
		}));
	}

	// Show the appointment confirmation popup, reusing the agenda's modal. The agenda id alone
	// is enough to load the confirmations (postAppointmentNewConfirmation); start-time is omitted
	// since the Enquiry app message only carries the appointment id.
	private showAppointmentConfirmation(appointmentId: number) {
		this.confirmAgendaId = appointmentId.toString();
		this.visibleConfirmAgenda = true;
	}

	// Show the Email out preview popup, reusing the history timeline's dialog. The history root id
	// alone is enough: the dialog fetches the email content itself (get-mailing-out) on open.
	private openEmailOut(historyRootId: number) {
		this.selectedEmailOut = { RootId: historyRootId };
		this.visibleEmailOut = true;
	}

	private openPropertyDetailDrawer(propertyId: number) {
		this.setVisibleDrawerModel(new DrawerModel({
			componentName: DRAWER_NAME.PROPERTY_DETAIL,
			props: { objectId: propertyId },
			title: 'PROPERTY',
		}));
	}

	private onTaskCreatedFromEnquiry(payload: { context?: string }) {
		if (!payload?.context || payload.context !== this.pendingTaskContext) return;
		this.pendingTaskContext = null;
		this.postMessageToIframe(ENQUIRY_RESPONSE.TASK_CREATED);
	}

	// The appointment form emits the same saved event for creates, edits and the
	// comment dialog; the context tells them apart so the Enquiry app gets the
	// right ack.
	private onAppointmentCreatedFromEnquiry(payload: { context?: string }) {
		if (!payload?.context) return;
		if (payload.context === this.pendingAppointmentContext) {
			this.pendingAppointmentContext = null;
			this.postMessageToIframe(ENQUIRY_RESPONSE.APPOINTMENT_CREATED);
		} else if (payload.context === this.pendingEditAppointmentContext) {
			this.pendingEditAppointmentContext = null;
			this.postMessageToIframe(ENQUIRY_RESPONSE.APPOINTMENT_UPDATED);
		} else if (payload.context === this.pendingCommentContext) {
			this.pendingCommentContext = null;
			this.postMessageToIframe(ENQUIRY_RESPONSE.APPOINTMENT_UPDATED);
		} else if (payload.context === this.pendingAddPersonContext) {
			this.pendingAddPersonContext = null;
			this.postMessageToIframe(ENQUIRY_RESPONSE.APPOINTMENT_UPDATED);
		}
	}

	// The add-person dialog's "New appointment" shortcut: the dialog sends the
	// group context it resolved itself (calendar user, property, first free
	// time after the group) — reuse the add-to-group drawer.
	// Not private: only the template calls it, which TS cannot see (TS6133).
	onAddPersonCreateNew(context: { userId: number, startTime: string, propertyId: number }) {
		if (!context?.userId || !context?.startTime || !context?.propertyId) return;
		this.openAddToGroupAppointmentDrawer(context);
	}

	// "Add to group appointment" from the embedded agenda's super-appointment popover:
	// open the New Appointment drawer in place (the user stays in the Enquiry Centre)
	// prefilled with the group's calendar user, property and next free time slot
	// only — no task/person prefill, the same behavior as the Add to group action
	// on the /agenda calendar's super-appointment popover.
	private openAddToGroupAppointmentDrawer(payload: { userId: number, startTime: string, propertyId: number }) {
		this.pendingAppointmentContext = `${ENQUIRY_APPOINTMENT_CONTEXT_PREFIX}-${Date.now()}`;

		this.setVisibleDrawerModel(new DrawerModel({
			componentName: DRAWER_NAME.APPOINTMENT_NEW_FORM,
			context: this.pendingAppointmentContext,
			props: {
				userId: payload.userId,
				startTime: payload.startTime,
				propertyId: payload.propertyId
			},
			title: 'NEW_APPOINTMENT',
			showSaveBtn: true,
		}));
	}

	// Print through the global print-template modal (mounted in the app header),
	// same as the agenda popover's print action. The appointments list is left
	// empty on purpose: printing runs on the SelectedIds lookup, and the modal's
	// informational listview requires full listing objects this host doesn't have.
	private printAppointment(appointmentId: number) {
		ReportHelper.initPrintTemplateModal({
			moduleTemplate: "Agenda",
			dataFields: [{ key: "Ids", value: [appointmentId] }],
			templateLookupValue: { Type: "Agenda", SelectedIds: [appointmentId] },
			lookupTemplateAction: { actionType: "", data: [] }
		});
		this.setPrintTemplateModalVisible({
			visible: true,
			props: { module: moduleConstant.Agenda, appointments: [] }
		});
	}

	// The comment dialog loads the appointment itself from {Id, StartTime, UserId}.
	// PropertyId and Person.Id must be real ids: the dialog resolves the comment
	// slot by matching them against the loaded appointment's property/person
	// lists. personId may be omitted only for person-less appointments (or a
	// single-person one, where the dialog defaults to that person); send it for
	// multi-person appointments or the comment lands in the person-less slot.
	// The names only feed its read-only header labels.
	private openAddCommentDialog(payload: { appointmentId: number, startTime: string, userId: number, propertyId: number, personId?: number, appointmentTypeId?: number, personName?: string, propertyName?: string }) {
		this.pendingCommentContext = `${ENQUIRY_EDIT_APPOINTMENT_CONTEXT_PREFIX}-comment-${Date.now()}`;
		this.addCommentEvent = {
			Id: payload.appointmentId,
			StartTime: payload.startTime,
			UserId: payload.userId,
			PropertyId: payload.propertyId,
			AppointmentTypeId: payload.appointmentTypeId || null,
			Person: payload.personId ? { Id: payload.personId, Name: payload.personName } : null,
			superPropertyName: payload.propertyName || ""
		};
		this.visibleAddComment = true;
	}

	private openCreateDemandDrawer(payload: { propertyId: number, personId: number }) {
		this.setVisibleDrawerModel(new DrawerModel({
			componentName: DRAWER_NAME.DEMAND_NEW_FORM,
			props: { data: { PropertyId: payload.propertyId, PersonId: payload.personId } },
			title: 'NEW_DEMAND',
			showSaveBtn: true
		}));
	}

	// Open the appointment edit form as a drawer so the user stays in the
	// Enquiry Centre; the saved ack lets the Enquiry app refresh its lists.
	private openEditAppointmentDrawer(appointmentId: number) {
		this.pendingEditAppointmentContext = `${ENQUIRY_EDIT_APPOINTMENT_CONTEXT_PREFIX}-${Date.now()}`;

		this.setVisibleDrawerModel(new DrawerModel({
			componentName: DRAWER_NAME.APPOINTMENT_EDIT_FORM,
			context: this.pendingEditAppointmentContext,
			props: { objectId: appointmentId },
			title: 'EDIT_APPOINTMENT',
			showSaveBtn: true,
		}));
	}

	private deleteAppointment(appointmentId: number) {
		// Not every locale's string carries the "|title" part (e.g. de-BE)
		const [message, title] = this.$t('DELETE_APPOINTMENT_CONFIRM').toString().split("|");
		this.$confirm(message, title || this.$t('WARNING').toString(), { type: "warning" })
			.then(() => this.agendaService.deleteAppointment(
				// The Enquiry popover only lists simple (non-recurring) appointments
				{ originId: appointmentId, masterId: appointmentId, isRecurrence: false, isException: false } as any,
				null, null, true))
			.then(() => this.postMessageToIframe(ENQUIRY_RESPONSE.APPOINTMENT_DELETED))
			.catch(error => {
				if (error !== 'cancel' && error !== 'close') this.$notify.error(error);
			});
	}

	// The add-person dialog fills an empty slot of a group appointment. The id
	// is all it needs: it loads the appointment itself and resolves the group
	// (title, slot list, "New appointment" shortcut context) from it.
	private openAddPersonDialog(appointmentId: number) {
		this.pendingAddPersonContext = `${ENQUIRY_EDIT_APPOINTMENT_CONTEXT_PREFIX}-addperson-${Date.now()}`;
		this.addPersonChild = { Id: appointmentId };
		this.visibleAddPerson = true;
	}

	handleMessage(event: MessageEvent) {
		if (event.origin !== ENQUIRY_CENTRE_ORIGIN) return;
		const iframe = getEnquiryCentreIframe();
		if (!iframe?.contentWindow || event.source !== iframe.contentWindow) return;

		const { data } = event;
		const type = data?.type;
		if (!type) return;
		switch (type) {
			case ENQUIRY_EVENT.CREATE_TASK: {
				const { props, title } = resolveCreateTaskDrawer(this.taskService, data?.data);
				this.openTaskDrawer(props, title);
				break;
			}
			case ENQUIRY_EVENT.CREATE_APPOINTMENT: {
				const taskId = data?.data?.taskId;
				if (!taskId) return;
				this.redirectToAgendaWithTask(Number(taskId));
				break;
			}
			case ENQUIRY_EVENT.OPEN_PERSON_DETAIL: {
				const personId = data?.data?.personId;
				if (!personId) return;
				this.openPersonDetailDrawer(Number(personId));
				break;
			}
			case ENQUIRY_EVENT.OPEN_PROPERTY_DETAIL: {
				const propertyId = data?.data?.propertyId;
				if (!propertyId) return;
				this.openPropertyDetailDrawer(Number(propertyId));
				break;
			}
			case ENQUIRY_EVENT.APPOINTMENT_CONFIRMATION: {
				const appointmentId = data?.data?.appointmentId;
				if (!appointmentId) return;
				this.showAppointmentConfirmation(Number(appointmentId));
				break;
			}
			case ENQUIRY_EVENT.ADD_TO_GROUP_APPOINTMENT: {
				const { userId, startTime, propertyId } = data?.data || {};
				if (!userId || !startTime || !propertyId) return;
				this.openAddToGroupAppointmentDrawer({
					userId: Number(userId),
					startTime: String(startTime),
					propertyId: Number(propertyId)
				});
				break;
			}
			case ENQUIRY_EVENT.PRINT_APPOINTMENT: {
				const appointmentId = data?.data?.appointmentId;
				if (!appointmentId) return;
				this.printAppointment(Number(appointmentId));
				break;
			}
			case ENQUIRY_EVENT.ADD_APPOINTMENT_COMMENT: {
				const { appointmentId, startTime, userId, propertyId, personId, appointmentTypeId, personName, propertyName } = data?.data || {};
				if (!appointmentId || !startTime || !userId || !propertyId) return;
				this.openAddCommentDialog({
					appointmentId: Number(appointmentId),
					startTime: String(startTime),
					userId: Number(userId),
					propertyId: Number(propertyId),
					personId: personId ? Number(personId) : null,
					appointmentTypeId: appointmentTypeId ? Number(appointmentTypeId) : null,
					personName,
					propertyName
				});
				break;
			}
			case ENQUIRY_EVENT.CREATE_DEMAND: {
				const { propertyId, personId } = data?.data || {};
				if (!propertyId || !personId) return;
				this.openCreateDemandDrawer({ propertyId: Number(propertyId), personId: Number(personId) });
				break;
			}
			case ENQUIRY_EVENT.EDIT_APPOINTMENT: {
				const appointmentId = data?.data?.appointmentId;
				if (!appointmentId) return;
				this.openEditAppointmentDrawer(Number(appointmentId));
				break;
			}
			case ENQUIRY_EVENT.DELETE_APPOINTMENT: {
				const appointmentId = data?.data?.appointmentId;
				if (!appointmentId) return;
				this.deleteAppointment(Number(appointmentId));
				break;
			}
			case ENQUIRY_EVENT.ADD_PERSON_TO_APPOINTMENT: {
				const appointmentId = data?.data?.appointmentId;
				if (!appointmentId) return;
				this.openAddPersonDialog(Number(appointmentId));
				break;
			}
			case ENQUIRY_EVENT.VIEW_EMAIL: {
				const historyRootId = data?.data?.historyRootId;
				if (!historyRootId) return;
				this.openEmailOut(Number(historyRootId));
				break;
			}
			default:
				break;
		}
	}

	beforeDestroy() {
		this.isComponentDestroyed = true;
		window.removeEventListener("message", this.handleMessage);
		EventBus.$off(EVENT_BUS.AGENDA.CREATE_NEW_TASK, this.onTaskCreatedFromEnquiry);
		EventBus.$off(EVENT_BUS.AGENDA.CREATE_NEW_APPOINTMENT, this.onAppointmentCreatedFromEnquiry);
	}
}
